import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lock, FileText, Download, Loader2, ShieldCheck, Unlock } from "lucide-react";
import { api } from "../lib/api";
import SectionHeading from "./SectionHeading";

const SAMPLE_CLIENT = [
  { k: "Prepared for", v: "Pooja M." },
  { k: "Contact", v: "+91 98230 XXXXX" },
  { k: "Goal", v: "Sustainable Lifestyle Recomp & Fat Loss" },
];

const PREVIEW = [
  { h: "Core Food Rules" },
  { p: "Avoid hybrid vegetables and packaged foods. No refined oils — ghee, butter, cold-pressed coconut, mustard or sesame only." },
  { p: "Rock salt / pink salt instead of iodised table salt. Zero added sugar, low glycaemic index foods only." },
  { h: "Before Breakfast" },
  { p: "1 tbsp apple cider vinegar in 250 ml water with a pinch of rock salt." },
  { h: "Breakfast" },
  { p: "Sprouted green moong / mataki / black chana — 150 g, or 2 eggs cooked in ghee only." },
  { p: "5 almonds + 5 walnuts — non-negotiable." },
  { h: "Lunch · 1 PM – 2 PM" },
  { p: "1–2 bowls raw or lightly steamed salad, eaten first. Protein: paneer / mushroom / egg — 120 g." },
  { p: "1 bhakri (no chapati) · 1 cup curd · low-GI fruit: blueberry, blackberry, dragon fruit or avocado." },
  { h: "Dinner · 6:30 PM – 7:30 PM" },
  { p: "Clear soup, 180 g vegetables, 3 egg whites or 50 g paneer, quinoa or moong chilla. No bhakri at night." },
];

export default function SamplePlan() {
  const [form, setForm] = useState({ full_name: "", contact: "" });
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fullUrl = downloadUrl ? `${process.env.REACT_APP_BACKEND_URL}${downloadUrl}` : "";

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api("/sample-plan/request", { method: "POST", body: JSON.stringify(form) });
      setDownloadUrl(res.download_url);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${res.download_url}`, "_blank", "noopener");
      toast.success("Unlocked! Your sample plan is opening in a new tab.");
    } catch (err) {
      toast.error(err.message || "Could not unlock the plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const unlocked = Boolean(downloadUrl);

  return (
    <section id="sample-plan" className="relative py-24 lg:py-32">
      <div aria-hidden="true" className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-gold/8 blur-[130px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="See The Work"
          title="Look Inside A Real Plan"
          sub="This is the exact format every 1-on-1 client receives — personal details removed. Unlock the full sample PDF and judge the depth of the work before you ever pay."
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-6 items-start">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative rounded-3xl border border-line bg-panel overflow-hidden"
            data-testid="sample-plan-preview"
          >
            <div className="flex items-center gap-3 border-b border-line px-7 py-4">
              <FileText className="h-4 w-4 text-emerald-400" />
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
                Holistic Nutrition Plan · Page 1
              </p>
            </div>
            <div
              className={`px-7 py-8 space-y-4 transition-all duration-700 ${
                unlocked ? "blur-0" : "blur-[5px] select-none"
              }`}
              aria-hidden={!unlocked}
            >
              <div className="mb-6 grid sm:grid-cols-3 gap-px rounded-2xl border border-line bg-line overflow-hidden">
                {SAMPLE_CLIENT.map((row) => (
                  <div key={row.k} className="bg-ink px-4 py-3">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{row.k}</p>
                    <p className="mt-1 text-sm font-semibold text-white leading-snug">{row.v}</p>
                  </div>
                ))}
              </div>
              {PREVIEW.map((row, i) =>
                row.h ? (
                  <p
                    key={i}
                    className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400 pt-3"
                  >
                    {row.h}
                  </p>
                ) : (
                  <p key={i} className="text-sm text-slate-300 leading-relaxed">
                    {row.p}
                  </p>
                )
              )}
            </div>
            <AnimatePresence>
              {!unlocked && (
                <motion.div
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 top-14 flex flex-col items-center justify-center bg-gradient-to-t from-ink via-ink/85 to-ink/40 text-center px-6"
                >
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
                    <Lock className="h-6 w-6 text-gold" />
                  </span>
                  <p className="mt-5 font-display text-2xl font-extrabold uppercase text-white">
                    Sample Plan Locked
                  </p>
                  <p className="mt-2 max-w-xs text-sm text-slate-400">
                    Enter your name and WhatsApp number to unlock the full PDF — free, no payment.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="lg:col-span-5 rounded-3xl border border-line bg-panel p-8 lg:p-10"
            data-testid="sample-plan-gate"
          >
            {unlocked ? (
              <div data-testid="sample-plan-unlocked">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 border border-accent/40">
                  <Unlock className="h-6 w-6 text-emerald-400" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase text-white">Plan Unlocked</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                  Didn't open automatically? Grab it here. Your own plan will look nothing like this one — it is
                  rebuilt around your body.
                </p>
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sample-plan-download-link"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-extrabold uppercase tracking-wide text-ink transition-all duration-300 hover:shadow-glow-emerald hover:-translate-y-0.5"
                >
                  <Download className="h-4 w-4" />
                  Download Sample PDF
                </a>
                <a
                  href="#intake-form"
                  data-testid="sample-plan-apply-link"
                  className="mt-4 block text-sm font-semibold text-gold hover:underline"
                >
                  Get my own personalised plan →
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="sample-plan-form">
                <div>
                  <h3 className="font-display text-2xl font-extrabold uppercase text-white">Unlock The Sample</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                    A branded PDF of a real client-format plan — core rules, meal timings, portions and lifestyle
                    protocol.
                  </p>
                </div>
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">
                    Full Name
                  </label>
                  <input
                    required
                    value={form.full_name}
                    onChange={set("full_name")}
                    placeholder="Pooja M."
                    data-testid="sample-plan-name-input"
                    className="w-full rounded-xl bg-ink border border-line px-4 py-3.5 text-white placeholder:text-slate-600 focus:border-accent focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2.5">
                    WhatsApp Number
                  </label>
                  <input
                    required
                    value={form.contact}
                    onChange={set("contact")}
                    placeholder="+91 98230 XXXXX"
                    data-testid="sample-plan-contact-input"
                    className="w-full rounded-xl bg-ink border border-line px-4 py-3.5 text-white placeholder:text-slate-600 focus:border-accent focus:outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="sample-plan-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-gold to-gold-dim px-7 py-4 font-extrabold uppercase tracking-wide text-ink transition-all duration-300 hover:shadow-glow-gold disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-4 w-4" />}
                  {loading ? "Unlocking..." : "Unlock Free Sample"}
                </button>
                <p className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-emerald-400/80" />
                  Used only so Nikhil can follow up once. No spam, no list selling, no automated marketing.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
